import Phaser from 'phaser'

export default class FieldScene extends Phaser.Scene {
  constructor() {
    super({ key: 'FieldScene' })
    this.teams = []
    this.props = []
    this.gridGraphics = []
    this.focusedTeamId = null
    this.focusedPropId = null
    this.fieldSettings = {
      showGrid: true,
      showNumbers: true,
      zoom: 1,
      panX: 0,
      panY: 0
    }
    this.onEntitySelect = null
    this.currentSelectedEntity = null
  }

  // Helper function to convert hex color string to numeric color
  hexToNumber(hex) {
    if (typeof hex === 'number') return hex
    if (typeof hex === 'string') {
      // Remove # if present
      hex = hex.replace('#', '')
      return parseInt(hex, 16)
    }
    return 0x000000 // Default to black if invalid
  }

  // Set focus on a team or prop for recording
  setFocus(teamId = null, propId = null) {
    this.focusedTeamId = teamId
    this.focusedPropId = propId
    
    // Clear any existing selection visuals when setting focus for recording
    if (teamId !== null || propId !== null) {
      this.clearAllSelectionVisuals()
      
      // Also clear the React state to prevent re-application of selection visuals
      if (this.entitySelectCallback) {
        this.entitySelectCallback(null)
      }
    }
    
    // Use a small delay to ensure teams/props are created before applying focus effects
    this.time.delayedCall(50, () => {
      this.updateFocusEffects()
    })
    
    // Disable drag functionality during countdown (when focus is set)
    // Re-enable when focus is cleared (countdown cancelled)
    this.setDragEnabled(teamId === null && propId === null)
  }

  // Convert screen coordinates to world coordinates using Phaser's camera
  // This is essential for accurate drop positioning when dragging from the palette
  screenToWorld(screenX, screenY) {
    const camera = this.cameras.main
    const worldPoint = camera.getWorldPoint(screenX, screenY)
    return { x: worldPoint.x, y: worldPoint.y }
  }

  // Enable or disable drag functionality for all teams and props
  // If allowedTargetId is provided, only that target will be draggable (for recording mode)
  setDragEnabled(enabled, allowedTargetId = null) {
    // Disable/enable team dragging
    this.teams.forEach(teamSprite => {
      if (teamSprite) {
        const teamId = teamSprite.teamData?.id
        const isAllowedTarget = allowedTargetId && teamId === allowedTargetId
        
        if (enabled) {
          // If recording mode (allowedTargetId provided), only allow the recording target
          if (allowedTargetId && !isAllowedTarget) {
            // Lock all other teams during recording
            teamSprite.disableInteractive()
          } else {
            // Re-enable full interactivity and dragging
            teamSprite.setInteractive({ useHandCursor: true })
            if (this.input) {
              this.input.setDraggable(teamSprite)
            }
          }
        } else {
          // Disable dragging but keep interactivity for the focused team
          const isFocusedTeam = this.focusedTeamId && teamId === this.focusedTeamId
          if (isFocusedTeam) {
            // Keep interactive for focused team (clickable but not draggable)
            teamSprite.setInteractive({ useHandCursor: false })
            if (this.input) {
              this.input.setDraggable(teamSprite, false)
            }
          } else {
            // Disable all interactivity for non-focused teams
            teamSprite.disableInteractive()
          }
        }
      }
    })
    
    // Disable/enable prop dragging
    this.props.forEach(propSprite => {
      if (propSprite) {
        const propId = propSprite.propData?.id
        const isAllowedTarget = allowedTargetId && propId === allowedTargetId
        
        if (enabled) {
          // If recording mode (allowedTargetId provided), only allow the recording target
          if (allowedTargetId && !isAllowedTarget) {
            // Lock all other props during recording
            propSprite.disableInteractive()
          } else {
            // Re-enable full interactivity and dragging
            propSprite.setInteractive({ useHandCursor: true })
            if (this.input) {
              this.input.setDraggable(propSprite)
            }
          }
        } else {
          // Disable dragging but keep interactivity for the focused prop
          const isFocusedProp = this.focusedPropId && propId === this.focusedPropId
          if (isFocusedProp) {
            // Keep interactive for focused prop (clickable but not draggable)
            propSprite.setInteractive({ useHandCursor: false })
            if (this.input) {
              this.input.setDraggable(propSprite, false)
            }
          } else {
            // Disable all interactivity for non-focused props
            propSprite.disableInteractive()
          }
        }
      }
    })
  }

  // Update visual focus effects
  updateFocusEffects() {
    // Update team focus effects
    this.teams.forEach(teamSprite => {
      if (teamSprite && teamSprite.teamData) {
        const isFocused = teamSprite.teamData.id === this.focusedTeamId
        if (isFocused) {
          // Add pulsing effect and thicker border
          teamSprite.setStrokeStyle(4, 0xFFFF00, 1.0) // Yellow border
          // Add pulsing animation
          this.tweens.add({
            targets: teamSprite,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
          })
        } else {
          // Reset to normal appearance
          teamSprite.setStrokeStyle(2, 0xFFFFFF, 1.0) // White border
          this.tweens.killTweensOf(teamSprite)
          teamSprite.setScale(1.0)
        }
      }
    })

    // Update prop focus effects
    this.props.forEach(propSprite => {
      if (propSprite && propSprite.propData) {
        const isFocused = propSprite.propData.id === this.focusedPropId
        if (isFocused) {
          // Check prop type to determine visual effect
          if (propSprite.propData.type === 'soccer-ball') {
            // For soccer ball, add a yellow glow effect using tint
            propSprite.setTint(0xFFFF00) // Yellow tint
          } else {
            // For other props (cones, circles), add thicker border
            if (typeof propSprite.setStrokeStyle === 'function') {
              propSprite.setStrokeStyle(4, 0xFFFF00, 1.0) // Yellow border
            }
          }
          
          // Add pulsing animation - very subtle for soccer ball
          const pulseScale = propSprite.propData.type === 'soccer-ball' ? 1.05 : 1.2
          
          // Calculate the target scale based on current scale
          const currentScaleX = propSprite.scaleX
          const currentScaleY = propSprite.scaleY
          const targetScaleX = currentScaleX * pulseScale
          const targetScaleY = currentScaleY * pulseScale
          
          this.tweens.add({
            targets: propSprite,
            scaleX: targetScaleX,
            scaleY: targetScaleY,
            duration: 500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
          })
        } else {
          // Reset to normal appearance
          if (propSprite.propData.type === 'soccer-ball') {
            // Reset soccer ball
            propSprite.clearTint() // Remove tint
            this.tweens.killTweensOf(propSprite)
            // Use original scale for soccer ball to preserve setDisplaySize
            if (propSprite.originalScaleX !== undefined && propSprite.originalScaleY !== undefined) {
              propSprite.setScale(propSprite.originalScaleX, propSprite.originalScaleY)
            } else {
              propSprite.setScale(1.0)
            }
          } else {
            // Reset other props (cones, circles)
            if (typeof propSprite.setStrokeStyle === 'function') {
              propSprite.setStrokeStyle(2, 0xFFFFFF, 1.0) // White border
            }
            this.tweens.killTweensOf(propSprite)
            propSprite.setScale(1.0)
          }
        }
      }
    })
  }

  preload() {
    // Create simple colored rectangles for teams
    this.load.image('team-home', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==')
    this.load.image('team-away', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==')
    
    // Load soccer ball image
    this.load.image('soccer-ball', '/soccer-ball.svg')
  }

  create() {
    const { width, height } = this.cameras.main
    
    // Create soccer field
    this.createField(width, height)
    
    // Always create grid (regardless of initial visibility) so we can toggle it later
    this.createGrid(width, height)
    
    // Set initial grid visibility based on settings
    if (this.gridGraphics) {
      this.gridGraphics.forEach(gridLine => {
        if (gridLine && gridLine.setVisible) {
          gridLine.setVisible(this.fieldSettings.showGrid)
        }
      })
    }
    
    // Set up camera controls
    this.setupCameraControls()
    
    // Handle window resize
    this.scale.on('resize', this.handleResize, this)
    
    // Fit field to screen first
    this.fitFieldToScreen()
    
    // Emit event that scene is ready after positioning is complete
    this.events.emit('sceneReady')
  }

  createField(width, height) {
    // Field background
    const fieldBg = this.add.rectangle(width / 2, height / 2, width, height, 0x22C55E)
    fieldBg.setDepth(-500) // Above pan area but below field markings
    
    const fieldType = this.fieldSettings.fieldType || 'full'
    
    if (fieldType === 'plain') {
      // Plain field - green background with simple outline
      this.fieldBounds = {
        x: 0,
        y: 0,
        width: width,
        height: height,
        centerX: width / 2,
        centerY: height / 2
      }
      
      // Add a simple border outline
      this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0).setDepth(-400)
      this.add.rectangle(width / 2, height / 2, width, height, 0xFFFFFF, 0).setStrokeStyle(4, 0xFFFFFF).setDepth(-400)
      
      return
    }
    
    if (fieldType === 'half') {
      // Half field - rotated 90 degrees counterclockwise, showing bottom half
      // Optimize dimensions to use more screen space
      const fieldWidth = Math.min(width * 0.95, height * 1.8) // Use more width and height
      const fieldHeight = fieldWidth * 0.8 // Stretch vertically more (was 0.67)
      const fieldX = width / 2
      const fieldY = height / 2
      
      // Store field bounds for reference
      this.fieldBounds = {
        x: fieldX - fieldWidth/2,
        y: fieldY - fieldHeight/2,
        width: fieldWidth,
        height: fieldHeight,
        centerX: fieldX,
        centerY: fieldY
      }
      
      // Field border (only bottom half)
      this.add.rectangle(fieldX, fieldY + fieldHeight/4, fieldWidth, fieldHeight/2, 0x000000, 0).setDepth(-400)
      this.add.rectangle(fieldX, fieldY + fieldHeight/4, fieldWidth, fieldHeight/2, 0xFFFFFF, 0).setStrokeStyle(4, 0xFFFFFF).setDepth(-400)
      
      // Center line (horizontal line - was vertical, now horizontal after rotation)
      this.add.rectangle(fieldX, fieldY, fieldWidth, 2, 0xFFFFFF).setDepth(-400)
      
      // No center circle for half field - removed for now
      
      // Goal areas (rotated - now at top and bottom instead of left and right)
      const goalWidth = fieldWidth * 0.2 // Swapped from fieldHeight
      const goalDepth = fieldHeight * 0.1 // Swapped from fieldWidth
      
      // Bottom goal area (6-yard box) - was right goal
      this.add.rectangle(fieldX, fieldY + fieldHeight/2 - goalDepth/2, goalWidth, goalDepth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
      // Bottom goal line
      this.add.rectangle(fieldX, fieldY + fieldHeight/2, goalWidth * 0.6, 4, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
      
      // Penalty areas (rotated)
      const penaltyWidth = fieldWidth * 0.4 // Swapped from fieldHeight
      const penaltyDepth = fieldHeight * 0.2 // Swapped from fieldWidth
      
      // Bottom penalty area (18-yard box) - was right penalty
      this.add.rectangle(fieldX, fieldY + fieldHeight/2 - penaltyDepth/2, penaltyWidth, penaltyDepth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
      
      // Penalty spots (rotated)
      const penaltySpotDistance = fieldHeight * 0.15 // Swapped from fieldWidth
      
      // Bottom penalty spot - was right penalty spot
      this.add.circle(fieldX, fieldY + fieldHeight/2 - penaltySpotDistance, 3, 0xFFFFFF).setDepth(-400)
      
      // Corner arcs (only bottom corners)
      const cornerRadius = 20
      
      // Bottom-left corner arc
      this.add.arc(fieldX - fieldWidth/2, fieldY + fieldHeight/2, cornerRadius, -Math.PI/2, 0, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
      
      // Bottom-right corner arc
      this.add.arc(fieldX + fieldWidth/2, fieldY + fieldHeight/2, cornerRadius, Math.PI, 3*Math.PI/2, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
      
      return
    }
    
    // Full field (default)
    // Field dimensions (proper soccer field proportions)
    const fieldWidth = Math.min(width * 0.9, height * 1.5) // 3:2 ratio
    const fieldHeight = fieldWidth * 0.67 // Maintain proper soccer field ratio
    const fieldX = width / 2
    const fieldY = height / 2
    
    // Store field bounds for reference
    this.fieldBounds = {
      x: fieldX - fieldWidth/2,
      y: fieldY - fieldHeight/2,
      width: fieldWidth,
      height: fieldHeight,
      centerX: fieldX,
      centerY: fieldY
    }
    
    // Field border
    this.add.rectangle(fieldX, fieldY, fieldWidth, fieldHeight, 0x000000, 0).setDepth(-400)
    this.add.rectangle(fieldX, fieldY, fieldWidth, fieldHeight, 0xFFFFFF, 0).setStrokeStyle(4, 0xFFFFFF).setDepth(-400)
    
    // Center line (vertical line through the middle of the field, touching both touchlines)
    this.add.rectangle(fieldX, fieldY, 2, fieldHeight, 0xFFFFFF).setDepth(-400)
    
    // Center circle
    this.add.circle(fieldX, fieldY, fieldHeight * 0.15, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Goal areas
    const goalWidth = fieldHeight * 0.2
    const goalDepth = fieldWidth * 0.1
    
    // Left goal area (6-yard box)
    this.add.rectangle(fieldX - fieldWidth/2 + goalDepth/2, fieldY, goalDepth, goalWidth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    // Left goal line
    this.add.rectangle(fieldX - fieldWidth/2, fieldY, 4, goalWidth * 0.6, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Right goal area (6-yard box)
    this.add.rectangle(fieldX + fieldWidth/2 - goalDepth/2, fieldY, goalDepth, goalWidth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    // Right goal line
    this.add.rectangle(fieldX + fieldWidth/2, fieldY, 4, goalWidth * 0.6, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Penalty areas
    const penaltyWidth = fieldHeight * 0.4
    const penaltyDepth = fieldWidth * 0.2
    
    // Left penalty area (18-yard box)
    this.add.rectangle(fieldX - fieldWidth/2 + penaltyDepth/2, fieldY, penaltyDepth, penaltyWidth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Right penalty area (18-yard box)
    this.add.rectangle(fieldX + fieldWidth/2 - penaltyDepth/2, fieldY, penaltyDepth, penaltyWidth, 0xFFFFFF, 0).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Penalty spots
    const penaltySpotDistance = fieldWidth * 0.15 // Distance from goal line
    
    // Left penalty spot
    this.add.circle(fieldX - fieldWidth/2 + penaltySpotDistance, fieldY, 3, 0xFFFFFF).setDepth(-400)
    
    // Right penalty spot
    this.add.circle(fieldX + fieldWidth/2 - penaltySpotDistance, fieldY, 3, 0xFFFFFF).setDepth(-400)
    
    // Corner arcs
    const cornerRadius = 20
    
    // Top-left corner arc
    this.add.arc(fieldX - fieldWidth/2, fieldY - fieldHeight/2, cornerRadius, 0, Math.PI/2, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Top-right corner arc
    this.add.arc(fieldX + fieldWidth/2, fieldY - fieldHeight/2, cornerRadius, Math.PI/2, Math.PI, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Bottom-left corner arc
    this.add.arc(fieldX - fieldWidth/2, fieldY + fieldHeight/2, cornerRadius, -Math.PI/2, 0, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
    
    // Bottom-right corner arc
    this.add.arc(fieldX + fieldWidth/2, fieldY + fieldHeight/2, cornerRadius, Math.PI, 3*Math.PI/2, false, 0xFFFFFF).setStrokeStyle(2, 0xFFFFFF).setDepth(-400)
  }

  createGrid(width, height) {
    const gridSize = 20
    const gridColor = 0xFFFFFF
    const gridAlpha = 0.1
    
    // Store grid graphics for easy visibility control
    this.gridGraphics = []
    
    const fieldType = this.fieldSettings.fieldType || 'full'
    
    if (fieldType === 'plain') {
      // For plain field, create grid across entire screen
      this.createSimpleGrid(0, 0, width, height, gridSize, gridColor, gridAlpha)
    } else {
      // For full field and half field, calculate field bounds
      let fieldWidth, fieldHeight, fieldX, fieldY
      
      if (fieldType === 'half') {
        fieldWidth = Math.min(width * 0.95, height * 1.8)
        fieldHeight = fieldWidth * 0.8
      } else {
        // Full field
        fieldWidth = Math.min(width * 0.9, height * 1.5)
        fieldHeight = fieldWidth * 0.67
      }
      
      fieldX = width / 2
      fieldY = height / 2
      
      // Calculate field boundaries
      const fieldLeft = fieldX - fieldWidth/2
      const fieldRight = fieldX + fieldWidth/2
      const fieldTop = fieldY - fieldHeight/2
      const fieldBottom = fieldY + fieldHeight/2
      
      if (fieldType === 'half') {
        // For half field, only show grid in the bottom half (below center line)
        const halfFieldTop = fieldY // Start from center line
        const halfFieldHeight = fieldHeight / 2 // Only bottom half
        this.createSimpleGrid(fieldLeft, halfFieldTop, fieldRight - fieldLeft, halfFieldHeight, gridSize, gridColor, gridAlpha)
      } else {
        // For full field, show grid across entire field
        this.createSimpleGrid(fieldLeft, fieldTop, fieldRight - fieldLeft, fieldBottom - fieldTop, gridSize, gridColor, gridAlpha)
      }
    }
  }
  
  createSimpleGrid(startX, startY, width, height, gridSize, color, alpha) {
    // Create vertical lines
    for (let x = startX; x <= startX + width; x += gridSize) {
      const line = this.add.graphics()
      line.lineStyle(1, color, alpha)
      line.beginPath()
      line.moveTo(x, startY)
      line.lineTo(x, startY + height)
      line.strokePath()
      line.setDepth(-300)
      // Store reference to grid graphics for visibility control
      this.gridGraphics.push(line)
    }
    
    // Create horizontal lines
    for (let y = startY; y <= startY + height; y += gridSize) {
      const line = this.add.graphics()
      line.lineStyle(1, color, alpha)
      line.beginPath()
      line.moveTo(startX, y)
      line.lineTo(startX + width, y)
      line.strokePath()
      line.setDepth(-300)
      // Store reference to grid graphics for visibility control
      this.gridGraphics.push(line)
    }
  }

  setupCameraControls() {
    // Enable camera controls for pan and zoom
    this.cameras.main.setZoom(this.fieldSettings.zoom)
    this.cameras.main.centerOn(this.fieldSettings.panX, this.fieldSettings.panY)
    
    // Set up camera bounds for panning (expanded bounds to allow panning)
    const { width, height } = this.cameras.main
    this.cameras.main.setBounds(-width, -height, width * 3, height * 3)
    
    // Add mouse wheel zoom
    this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
      const zoomFactor = 0.1
      const currentZoom = this.cameras.main.zoom
      const newZoom = deltaY > 0 
        ? Math.max(0.3, currentZoom - zoomFactor) 
        : Math.min(3, currentZoom + zoomFactor)
      
      this.cameras.main.setZoom(newZoom)
      this.fieldSettings.zoom = newZoom
    })
    
    // Set up pan area
    this.setupPanArea(width, height)
  }

  setupPanArea(width, height) {
    // Create a large transparent background for panning
    const panArea = this.add.rectangle(width/2, height/2, width * 2, height * 2, 0x000000, 0)
    panArea.setInteractive()
    panArea.setDepth(-1000) // Behind everything else
    
    // Add smooth pan controls to the background
    let isPanning = false
    let lastPanX = 0
    let lastPanY = 0
    
    panArea.on('pointerdown', (pointer) => {
      // Clear selection when clicking on empty field
      if (this.onEntitySelect) {
        this.onEntitySelect(null)
      }
      
      isPanning = true
      lastPanX = pointer.x
      lastPanY = pointer.y
      this.input.setDefaultCursor('grabbing')
    })
    
    panArea.on('pointermove', (pointer) => {
      if (isPanning && pointer.isDown) {
        const deltaX = pointer.x - lastPanX
        const deltaY = pointer.y - lastPanY
        
        // Apply smooth panning with zoom consideration
        const panSpeed = 1.0 / this.cameras.main.zoom
        this.cameras.main.scrollX -= deltaX * panSpeed
        this.cameras.main.scrollY -= deltaY * panSpeed
        
        lastPanX = pointer.x
        lastPanY = pointer.y
        
        this.fieldSettings.panX = this.cameras.main.scrollX
        this.fieldSettings.panY = this.cameras.main.scrollY
      }
    })
    
    panArea.on('pointerup', () => {
      isPanning = false
      this.input.setDefaultCursor('default')
    })
    
    // Global pointer up to ensure panning stops
    this.input.on('pointerup', () => {
      isPanning = false
      this.input.setDefaultCursor('default')
    })
  }

  updateFieldSettings(settings) {
    const oldFieldType = this.fieldSettings.fieldType
    this.fieldSettings = { ...this.fieldSettings, ...settings }
    
    // If field type changed, recreate the field
    if (settings.fieldType !== undefined && settings.fieldType !== oldFieldType) {
      this.recreateField()
      return
    }
    
    // Update grid visibility
    if (settings.showGrid !== undefined) {
      // Store current camera state to prevent automatic adjustments when grid becomes visible
      const currentZoom = this.cameras.main.zoom
      const currentScrollX = this.cameras.main.scrollX
      const currentScrollY = this.cameras.main.scrollY
      
      // Toggle grid visibility
      if (this.gridGraphics) {
        this.gridGraphics.forEach(gridLine => {
          if (gridLine && gridLine.setVisible) {
            gridLine.setVisible(settings.showGrid)
          }
        })
      }
      
      // Preserve camera position (Phaser may auto-adjust when objects become visible)
      this.cameras.main.setZoom(currentZoom)
      this.cameras.main.setScroll(currentScrollX, currentScrollY)
      
      // Ensure camera position is maintained after any potential auto-adjustments
      this.time.delayedCall(10, () => {
        this.cameras.main.setZoom(currentZoom)
        this.cameras.main.setScroll(currentScrollX, currentScrollY)
      })
    }
    
    // Update team number visibility
    if (settings.showNumbers !== undefined) {
      this.teams.forEach(teamSprite => {
        if (teamSprite.numberText) {
          teamSprite.numberText.setVisible(settings.showNumbers)
        }
      })
    }
    
    // Update camera
    if (settings.zoom !== undefined) {
      this.cameras.main.setZoom(settings.zoom)
      this.fieldSettings.zoom = settings.zoom // Keep local settings in sync
    }
    
    if (settings.panX !== undefined || settings.panY !== undefined) {
      this.cameras.main.centerOn(settings.panX, settings.panY)
      // Keep local settings in sync
      if (settings.panX !== undefined) this.fieldSettings.panX = settings.panX
      if (settings.panY !== undefined) this.fieldSettings.panY = settings.panY
    }
  }

  recreateField() {
    const { width, height } = this.cameras.main
    
    // Clear grid graphics array
    this.gridGraphics = []
    
    // Clear all field-related elements (background, markings, grid, pan area)
    // Keep only teams (depth 100) and props (depth 100)
    const childrenToDestroy = []
    
    this.children.list.forEach(child => {
      // Destroy field elements (background, borders, lines, circles, grid, pan area)
      if (child.depth <= -300 || child.depth === -1000) {
        childrenToDestroy.push(child)
      }
    })
    
    // Destroy all field elements and pan area
    childrenToDestroy.forEach(child => {
      child.destroy()
    })
    
    // Recreate the field
    this.createField(width, height)
    
    // Always recreate grid (regardless of initial visibility) so we can toggle it later
    this.createGrid(width, height)
    
    // Set initial grid visibility based on settings
    if (this.gridGraphics) {
      this.gridGraphics.forEach(gridLine => {
        if (gridLine && gridLine.setVisible) {
          gridLine.setVisible(this.fieldSettings.showGrid)
        }
      })
    }
    
    // Recreate pan area for the new field type
    this.setupPanArea(width, height)
    
    // Fit field to screen after recreation
    this.fitFieldToScreen()
  }

  fitFieldToScreen() {
    const { width, height } = this.cameras.main
    
    // Calculate the field dimensions based on field type
    let fieldWidth, fieldHeight
    const fieldType = this.fieldSettings.fieldType || 'full'
    
    if (fieldType === 'half') {
      fieldWidth = Math.min(width * 0.95, height * 1.8)
      fieldHeight = fieldWidth * 0.8
    } else if (fieldType === 'plain') {
      fieldWidth = width
      fieldHeight = height
    } else {
      // Full field (default)
      fieldWidth = Math.min(width * 0.9, height * 1.5)
      fieldHeight = fieldWidth * 0.67
    }
    
    // Calculate zoom to fit the field in the viewport
    const zoomX = width / fieldWidth
    const zoomY = height / fieldHeight
    let fitZoom = Math.min(zoomX, zoomY) * 0.9 // 90% to leave some margin
    
    // Special handling for half field - zoom in more and center better
    let centerX = width / 2
    let centerY = height / 2
    
    if (fieldType === 'half') {
      // Zoom in much more for half field (use 50% for very tight fit)
      fitZoom = Math.min(zoomX, zoomY) * 1.5
      // Center the half field better - move camera down slightly to show more of the field
      centerY = height / 2 + height * 0.3 // Move down 10% to show more field
    }
    
    // Apply the fit
    this.cameras.main.setZoom(fitZoom)
    this.cameras.main.centerOn(centerX, centerY)
    
    // Update local settings
    this.fieldSettings.zoom = fitZoom
    this.fieldSettings.panX = centerX
    this.fieldSettings.panY = centerY
    
    // Ensure camera bounds are properly set for panning
    this.cameras.main.setBounds(-width, -height, width * 3, height * 3)
    
  }

  updateTeams(teams) {
    // Store current visual positions before destroying sprites
    const currentPositions = {}
    this.teams.forEach(teamSprite => {
      if (teamSprite && teamSprite.teamData) {
        currentPositions[teamSprite.teamData.id] = {
          x: teamSprite.x,
          y: teamSprite.y
        }
      }
    })
    
    // Set flag to indicate teams were just updated
    this.teamsJustUpdated = true
    
    // Remove existing team sprites
    this.teams.forEach(teamSprite => {
      if (teamSprite) {
        // Clear selection visual before destroying
        this.clearSpriteSelection(teamSprite)
        if (teamSprite.numberText) {
          teamSprite.numberText.destroy()
        }
        teamSprite.destroy()
      }
    })
    
    this.teams = []
    
    // Create new team sprites
    teams.forEach(team => {
      // Use current visual position if available, otherwise use store position
      let startX = team.x
      let startY = team.y
      
      // If we have a current visual position, use that instead of store position
      const hasPreservedPosition = currentPositions[team.id]
      if (hasPreservedPosition) {
        startX = currentPositions[team.id].x
        startY = currentPositions[team.id].y
      }
      
      // Only apply animation calculations if we don't have a preserved position
      // (i.e., this is a new team, not an existing one being recreated)
      if (!hasPreservedPosition) {
        // Check if this team has an active animation
        // Don't apply animations when currentTime is 0 (initial state)
        const activeAnimation = this.currentTime > 0 ? this.currentAnimations?.find(anim => 
          anim.teamId === team.id && 
          this.currentTime >= anim.startTime && 
          this.currentTime <= anim.endTime
        ) : null
        
        if (activeAnimation) {
        // Calculate position based on current animation progress
        const progress = (this.currentTime - activeAnimation.startTime) / activeAnimation.duration
        
        if (activeAnimation.path && activeAnimation.path.length > 0) {
          // Interpolate along the path
          const pathIndex = progress * (activeAnimation.path.length - 1)
          const lowerIndex = Math.floor(pathIndex)
          const upperIndex = Math.min(lowerIndex + 1, activeAnimation.path.length - 1)
          
          if (lowerIndex === upperIndex) {
            const point = activeAnimation.path[lowerIndex]
            if (point && typeof point.x === 'number' && typeof point.y === 'number') {
              startX = point.x
              startY = point.y
            }
          } else {
            const lowerPoint = activeAnimation.path[lowerIndex]
            const upperPoint = activeAnimation.path[upperIndex]
            
            if (lowerPoint && upperPoint && 
                typeof lowerPoint.x === 'number' && typeof lowerPoint.y === 'number' &&
                typeof upperPoint.x === 'number' && typeof upperPoint.y === 'number') {
              const localProgress = pathIndex - lowerIndex
              
              startX = lowerPoint.x + (upperPoint.x - lowerPoint.x) * localProgress
              startY = lowerPoint.y + (upperPoint.y - lowerPoint.y) * localProgress
            }
          }
        } else {
          // Simple linear interpolation
          startX = activeAnimation.startX + (activeAnimation.endX - activeAnimation.startX) * progress
          startY = activeAnimation.startY + (activeAnimation.endY - activeAnimation.startY) * progress
        }
        }
      }
      
      const teamSprite = this.add.circle(
        startX, 
        startY, 
        15, 
        Phaser.Display.Color.HexStringToColor(team.color).color
      )
      
      // Set full opacity to prevent field bleed-through
      teamSprite.setAlpha(1.0)
      
      // Add a subtle border to make teams more visible
      teamSprite.setStrokeStyle(2, 0xFFFFFF, 1.0)
      
      // Add team number text
      if (this.fieldSettings.showNumbers && team.number) {
        const numberText = this.add.text(startX, startY, team.number, {
          fontSize: '12px',
          color: '#FFFFFF',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5, 0.5).setDepth(101) // Above the team
        
        teamSprite.numberText = numberText
      }
      
      // Make team interactive
      teamSprite.setInteractive({ useHandCursor: true })
      teamSprite.teamData = team
      teamSprite.setDepth(100) // Above the pan area
      
      // Add drag functionality based on focus state and recording state
      if (this.isRecording) {
        // Recording mode - only the recording target is draggable
        if (this.recordingTeamId === team.id) {
          // Recording target - enable dragging
          this.input.setDraggable(teamSprite)
        } else {
          // Not the recording target - lock it
          teamSprite.disableInteractive()
        }
      } else if (!this.focusedTeamId && !this.focusedPropId) {
        // Normal mode - full drag functionality
        this.input.setDraggable(teamSprite)
      } else if (this.focusedTeamId === team.id) {
        // Countdown mode - focused team is clickable but not draggable
        teamSprite.setInteractive({ useHandCursor: false })
        this.input.setDraggable(teamSprite, false)
      } else {
        // Countdown mode - non-focused teams are not interactive
        teamSprite.disableInteractive()
      }
      
      // Add click events for selection
      teamSprite.on('pointerdown', (pointer) => {
        if (pointer.rightButtonDown()) {
          // Right click - remove team
          this.removeTeamFromStore(team.id)
          return
        }
        
        // Left click - select team
        if (this.onEntitySelect) {
          this.onEntitySelect({ type: 'team', id: team.id })
        }
        
        this.teams.forEach(t => {
          if (t !== teamSprite) {
            t.setAlpha(0.7) // Dim other teams
            t.setScale(1.0) // Reset scale for unselected teams
            // Ensure number text follows unselected teams
            if (t.numberText) {
              t.numberText.x = t.x
              t.numberText.y = t.y
            }
          }
        })
        teamSprite.setAlpha(1.0) // Keep selected team bright
        teamSprite.setScale(1.2) // Make selected team slightly larger
        // Ensure number text follows selected team
        if (teamSprite.numberText) {
          teamSprite.numberText.x = teamSprite.x
          teamSprite.numberText.y = teamSprite.y
        }
        
        // Stop event propagation to prevent panning when clicking on team
        pointer.event.stopPropagation()
      })
      
      teamSprite.on('dragstart', () => {
        // Stop panning when starting to drag a team
        // Note: isPanning is local to setupCameraControls, so we can't access it here
        // The panning will be handled by the pan area's event system
      })
      
      teamSprite.on('drag', (pointer, dragX, dragY) => {
        teamSprite.x = dragX
        teamSprite.y = dragY
        
        if (teamSprite.numberText) {
          teamSprite.numberText.x = dragX
          teamSprite.numberText.y = dragY
        }
        
        // Update selection border position if sprite is selected
        if (teamSprite.selectionBorder) {
          teamSprite.selectionBorder.clear()
          teamSprite.selectionBorder.lineStyle(3, 0x00FF00, 1) // Green border
          teamSprite.selectionBorder.strokeCircle(dragX, dragY, 20)
        }
        
        // If we're recording, add this position to the recording path
        if (this.isRecording && this.recordingTeamId === team.id) {
          this.addToRecordingPath(dragX, dragY)
        } else {
          // Only update store position when NOT recording to avoid conflicts with animations
          this.updateTeamPositionInStore(team.id, dragX, dragY)
        }
      })
      
      teamSprite.on('dragend', (pointer) => {
        // Reset visual state
        this.teams.forEach(t => {
          t.setAlpha(1.0)
          t.setScale(1.0)
        })
      })
      
      // Add hover effects
      teamSprite.on('pointerover', () => {
        teamSprite.setScale(1.1)
        // Ensure number text follows during hover
        if (teamSprite.numberText) {
          teamSprite.numberText.x = teamSprite.x
          teamSprite.numberText.y = teamSprite.y
        }
      })
      
      teamSprite.on('pointerout', () => {
        if (!teamSprite.input.isDragged) {
          teamSprite.setScale(1.0)
        }
        // Ensure number text follows after hover
        if (teamSprite.numberText) {
          teamSprite.numberText.x = teamSprite.x
          teamSprite.numberText.y = teamSprite.y
        }
      })
      
      this.teams.push(teamSprite)
    })
    
    // Ensure all number texts are positioned correctly after team update
    this.syncNumberTextPositions()
    
    // Selection visuals are automatically cleared when sprites are destroyed above
  }

  updateProps(props) {
    // Store current visual positions before destroying sprites
    const currentPositions = {}
    this.props.forEach(propSprite => {
      if (propSprite && propSprite.propData) {
        currentPositions[propSprite.propData.id] = {
          x: propSprite.x,
          y: propSprite.y
        }
      }
    })
    
    // Remove existing prop sprites
    this.props.forEach(propSprite => {
      if (propSprite) {
        // Clear selection visual before destroying
        this.clearSpriteSelection(propSprite)
        propSprite.destroy()
      }
    })
    
    this.props = []
    
    // Create new prop sprites
    props.forEach(prop => {
      // Use current visual position if available, otherwise use store position
      let startX = prop.x
      let startY = prop.y
      
      // If we have a current visual position, use that instead of store position
      if (currentPositions[prop.id]) {
        startX = currentPositions[prop.id].x
        startY = currentPositions[prop.id].y
      }
      
      // Create prop sprite based on type
      let propSprite
      if (prop.type === 'cone') {
        // Create cone shape (triangle)
        propSprite = this.add.polygon(startX, startY, [
          0, -15,  // Top point
          -10, 10, // Bottom left
          10, 10   // Bottom right
        ], this.hexToNumber(prop.color))
        
        // Set full opacity and add border for cone
        propSprite.setAlpha(1.0)
        propSprite.setStrokeStyle(2, 0xFFFFFF, 1.0)
        
        // Set explicit interactive area for the cone polygon
        propSprite.setInteractive({
          useHandCursor: true,
          hitArea: new Phaser.Geom.Polygon([
            0, -15,  // Top point
            -10, 10, // Bottom left
            10, 10   // Bottom right
          ]),
          hitAreaCallback: Phaser.Geom.Polygon.Contains
        })
      } else if (prop.type === 'soccer-ball') {
        // Create soccer ball sprite using the PNG image
        console.log('Creating soccer ball sprite at:', startX, startY)
        propSprite = this.add.image(startX, startY, 'soccer-ball')
        console.log('Soccer ball sprite created:', propSprite)
        
        // Check if the image loaded successfully
        if (propSprite && propSprite.texture) {
          console.log('Soccer ball texture loaded successfully')
          // Resize the soccer ball to appropriate size (45x45 pixels - 87.5% larger than original)
          propSprite.setDisplaySize(45, 45)
          // Set full opacity for soccer ball image
          propSprite.setAlpha(1.0)
        } else {
          // Fallback to circle if image didn't load
          console.warn('Soccer ball image not loaded, using fallback circle')
          propSprite = this.add.circle(startX, startY, 22.5, this.hexToNumber(prop.color))
          // Set full opacity and add border for fallback circle
          propSprite.setAlpha(1.0)
          propSprite.setStrokeStyle(2, 0xFFFFFF, 1.0)
        }
      } else {
        // Default circle for unknown prop types
        propSprite = this.add.circle(startX, startY, 10, this.hexToNumber(prop.color))
        // Set full opacity and add border for default circle
        propSprite.setAlpha(1.0)
        propSprite.setStrokeStyle(2, 0xFFFFFF, 1.0)
      }
      
      // Make all props interactive and draggable (check if sprite exists)
      if (propSprite) {
        // Skip setting interactive for cones since they have explicit hit area
        if (prop.type !== 'cone') {
          propSprite.setInteractive({ useHandCursor: true })
        }
        propSprite.propData = prop
        
        // Store original scale for image objects to preserve setDisplaySize
        if (prop.type === 'soccer-ball' && propSprite.setDisplaySize) {
          propSprite.originalScaleX = propSprite.scaleX
          propSprite.originalScaleY = propSprite.scaleY
        }
      }
      
      // Add drag functionality for all props (check if sprite exists and input is available)
      if (propSprite && this.input) {
        try {
          if (this.isRecording) {
            // Recording mode - only the recording target is draggable
            if (this.recordingTeamId === prop.id) {
              // Recording target - enable dragging
              this.input.setDraggable(propSprite)
            } else {
              // Not the recording target - lock it
              propSprite.disableInteractive()
            }
          } else if (!this.focusedTeamId && !this.focusedPropId) {
            // Normal mode - full drag functionality
            this.input.setDraggable(propSprite)
          } else if (this.focusedPropId === prop.id) {
            // Countdown mode - focused prop is clickable but not draggable
            propSprite.setInteractive({ useHandCursor: false })
            this.input.setDraggable(propSprite, false)
          } else {
            // Countdown mode - non-focused props are not interactive
            propSprite.disableInteractive()
          }
        } catch (error) {
          console.error('Error setting draggable for prop:', prop.type, error)
        }
      } else {
        console.warn('Cannot set draggable - propSprite:', !!propSprite, 'input:', !!this.input)
      }
      
      if (propSprite) {
        propSprite.on('drag', (pointer, dragX, dragY) => {
          propSprite.x = dragX
          propSprite.y = dragY
          
          // Update selection border position if sprite is selected
          if (propSprite.selectionBorder) {
            propSprite.selectionBorder.clear()
            propSprite.selectionBorder.lineStyle(3, 0x00FF00, 1) // Green border
            
            // Handle different sprite types for border positioning
            if (propSprite.propData?.type === 'cone') {
              // For cones, draw a circle that better fits the triangular shape
              propSprite.selectionBorder.strokeCircle(dragX - 10, dragY - 13, 18) // Offset up and left, smaller radius
            } else {
              // For all other sprites (soccer ball), use standard circle
              propSprite.selectionBorder.strokeCircle(dragX, dragY, 20)
            }
          }
          
          // If we're recording, add this position to the recording path
          if (this.isRecording && this.recordingTeamId === prop.id) {
            this.addToRecordingPath(dragX, dragY)
          } else {
            // Only update store position when NOT recording to avoid conflicts with animations
            this.updatePropPositionInStore(prop.id, dragX, dragY)
          }
        })
        
        propSprite.on('dragend', (pointer) => {
          // Reset visual state
          this.props.forEach(p => {
            if (p && p.setAlpha && p.setScale) {
              p.setAlpha(1.0)
              // Don't reset scale for soccer balls as they have custom display size
              if (p.propData && p.propData.type !== 'soccer-ball') {
                p.setScale(1.0)
              }
            }
          })
        })
        
        // Add hover effects
        propSprite.on('pointerover', () => {
          if (propSprite && propSprite.setScale) {
            // Skip hover scaling for soccer balls since they use setDisplaySize
            if (prop.type !== 'soccer-ball') {
              propSprite.setScale(1.1)
            }
          }
        })
        
        propSprite.on('pointerout', () => {
          if (propSprite && propSprite.input && !propSprite.input.isDragged && propSprite.setScale) {
            // For soccer balls, don't reset scale as they have custom display size
            if (prop.type !== 'soccer-ball') {
              propSprite.setScale(1.0)
            }
          }
        })
        
        // Add right-click to remove
        propSprite.on('pointerdown', (pointer) => {
          if (pointer.rightButtonDown()) {
            this.removePropFromStore(prop.id)
          } else {
            // Left click - select prop
            if (this.onEntitySelect) {
              this.onEntitySelect({ type: 'prop', id: prop.id })
            }
          }
        })
      }
      
      if (propSprite) {
        propSprite.setDepth(50) // Below teams but above field
        this.props.push(propSprite)
      }
    })
    
    // Selection visuals are automatically cleared when sprites are destroyed above
  }

  updateAnimations(animations, currentTime) {
    // Store current animations and time for use in updateTeams
    this.currentAnimations = animations
    this.currentTime = currentTime
    
    // If teams were just updated, skip animation updates to preserve their visual positions
    if (this.teamsJustUpdated) {
      console.log('Skipping animation updates - teams just updated')
      this.teamsJustUpdated = false
      return
    }
    
    console.log('Running animation updates for time:', currentTime)
    
    // Group animations by entity (teamId or propId) to handle multiple animations per entity
    const animationsByEntity = {}
    animations.forEach(animation => {
      const entityId = animation.teamId || animation.propId
      if (!entityId) return
      
      if (!animationsByEntity[entityId]) {
        animationsByEntity[entityId] = {
          teamId: animation.teamId,
          propId: animation.propId,
          animations: []
        }
      }
      animationsByEntity[entityId].animations.push(animation)
    })
    
    // Process each entity's animations
    Object.values(animationsByEntity).forEach(entityAnimations => {
      const { teamId, propId, animations: entityAnims } = entityAnimations
      
      // Find the target sprite
      let targetSprite = null
      let isTeam = false
      
      if (teamId) {
        targetSprite = this.teams.find(t => t.teamData.id === teamId)
        isTeam = true
      } else if (propId) {
        targetSprite = this.props.find(p => p.propData.id === propId)
        isTeam = false
      }
      
      if (!targetSprite) {
        return
      }
      
      // Skip animation updates if we're currently recording this entity
      // This prevents animation playback from interfering with drag recording
      const entityId = teamId || propId
      if (this.isRecording && this.recordingTeamId === entityId) {
        return
      }
      
      // Sort animations by startTime to handle sequential animations correctly
      const sortedAnims = [...entityAnims].sort((a, b) => a.startTime - b.startTime)
      
      // Find the active animation at current time
      const activeAnimation = sortedAnims.find(anim => 
        currentTime >= anim.startTime && currentTime <= anim.endTime
      )
      
      if (activeAnimation) {
        // An animation is active - update position based on it
        const animation = activeAnimation
        const relativeTime = currentTime - animation.startTime
        
        if (animation.path && animation.path.length > 0) {
          // Get the first and last path point times to determine the actual path time range
          const firstPathTime = animation.path[0]?.time ?? 0
          const lastPathTime = animation.path[animation.path.length - 1]?.time ?? animation.duration
          
          // If current time is before the first path point, keep sprite at starting position
          if (relativeTime < firstPathTime) {
            targetSprite.x = animation.startX
            targetSprite.y = animation.startY
          } else if (relativeTime >= lastPathTime) {
            // If current time is after the last path point, use the last path point position
            const lastPoint = animation.path[animation.path.length - 1]
            if (lastPoint && typeof lastPoint.x === 'number' && typeof lastPoint.y === 'number') {
              targetSprite.x = lastPoint.x
              targetSprite.y = lastPoint.y
            }
          } else {
            // Find the two path points to interpolate between based on timestamps
            let lowerIndex = 0
            let upperIndex = animation.path.length - 1
            
            // Binary search for the correct segment
            for (let i = 0; i < animation.path.length - 1; i++) {
              const currentPointTime = animation.path[i]?.time ?? 0
              const nextPointTime = animation.path[i + 1]?.time ?? animation.duration
              
              if (relativeTime >= currentPointTime && relativeTime <= nextPointTime) {
                lowerIndex = i
                upperIndex = i + 1
                break
              }
            }
            
            const lowerPoint = animation.path[lowerIndex]
            const upperPoint = animation.path[upperIndex]
            
            if (lowerPoint && upperPoint && 
                typeof lowerPoint.x === 'number' && typeof lowerPoint.y === 'number' &&
                typeof upperPoint.x === 'number' && typeof upperPoint.y === 'number') {
              
              // Calculate interpolation progress based on timestamps
              const lowerTime = lowerPoint.time ?? 0
              const upperTime = upperPoint.time ?? animation.duration
              const timeRange = upperTime - lowerTime
              
              if (timeRange > 0) {
                const localProgress = (relativeTime - lowerTime) / timeRange
                targetSprite.x = lowerPoint.x + (upperPoint.x - lowerPoint.x) * localProgress
                targetSprite.y = lowerPoint.y + (upperPoint.y - lowerPoint.y) * localProgress
              } else {
                // If timestamps are the same, use the lower point
                targetSprite.x = lowerPoint.x
                targetSprite.y = lowerPoint.y
              }
            }
          }
        } else {
          // Simple linear interpolation (no path, use start/end positions)
          const progress = relativeTime / animation.duration
          targetSprite.x = animation.startX + (animation.endX - animation.startX) * progress
          targetSprite.y = animation.startY + (animation.endY - animation.startY) * progress
        }
        
        // Update number text position after team position is set (only for teams)
        if (isTeam && targetSprite.numberText) {
          targetSprite.numberText.x = targetSprite.x
          targetSprite.numberText.y = targetSprite.y
        }
        
        // Update selection border position if sprite is selected
        if (targetSprite.selectionBorder) {
          // Clear the old border
          targetSprite.selectionBorder.clear()
          
          // Redraw the border at the new position
          targetSprite.selectionBorder.lineStyle(3, 0x00FF00, 1) // Green border
          
          // Handle different sprite types for border positioning
          if (targetSprite.propData?.type === 'cone') {
            // For cones, draw a circle that better fits the triangular shape
            targetSprite.selectionBorder.strokeCircle(targetSprite.x - 10, targetSprite.y - 13, 18)
          } else {
            // For all other sprites (teams, soccer ball), use standard circle
            targetSprite.selectionBorder.strokeCircle(targetSprite.x, targetSprite.y, 20)
          }
        }
      } else {
        // No animation is active - determine correct position based on timeline
        const firstAnim = sortedAnims[0]
        const lastAnim = sortedAnims[sortedAnims.length - 1]
        
        let targetX, targetY
        
        if (currentTime < firstAnim.startTime) {
          // Before first animation - use starting position of first animation
          targetX = firstAnim.startX
          targetY = firstAnim.startY
        } else if (currentTime > lastAnim.endTime) {
          // After last animation - use ending position of last animation
          if (lastAnim.path && lastAnim.path.length > 0) {
            const lastPoint = lastAnim.path[lastAnim.path.length - 1]
            targetX = lastPoint?.x ?? lastAnim.endX
            targetY = lastPoint?.y ?? lastAnim.endY
          } else {
            targetX = lastAnim.endX
            targetY = lastAnim.endY
          }
        } else {
          // Between animations - find the previous completed animation and use its end position
          const previousAnim = sortedAnims
            .filter(anim => anim.endTime <= currentTime)
            .sort((a, b) => b.endTime - a.endTime)[0]
          
          if (previousAnim) {
            if (previousAnim.path && previousAnim.path.length > 0) {
              const lastPoint = previousAnim.path[previousAnim.path.length - 1]
              targetX = lastPoint?.x ?? previousAnim.endX
              targetY = lastPoint?.y ?? previousAnim.endY
            } else {
              targetX = previousAnim.endX
              targetY = previousAnim.endY
            }
          } else {
            // Fallback to first animation start position
            targetX = firstAnim.startX
            targetY = firstAnim.startY
          }
        }
        
        // Update sprite position
        targetSprite.x = targetX
        targetSprite.y = targetY
        
        // Update number text position after team position is set (only for teams)
        if (isTeam && targetSprite.numberText) {
          targetSprite.numberText.x = targetSprite.x
          targetSprite.numberText.y = targetSprite.y
        }
        
        // Update selection border position if sprite is selected
        if (targetSprite.selectionBorder) {
          // Clear the old border
          targetSprite.selectionBorder.clear()
          
          // Redraw the border at the new position
          targetSprite.selectionBorder.lineStyle(3, 0x00FF00, 1) // Green border
          
          // Handle different sprite types for border positioning
          if (targetSprite.propData?.type === 'cone') {
            // For cones, draw a circle that better fits the triangular shape
            targetSprite.selectionBorder.strokeCircle(targetSprite.x - 10, targetSprite.y - 13, 18)
          } else {
            // For all other sprites (teams, soccer ball), use standard circle
            targetSprite.selectionBorder.strokeCircle(targetSprite.x, targetSprite.y, 20)
          }
        }
      }
    })
  }

  syncNumberTextPositions() {
    // Ensure all number texts are positioned correctly with their team sprites
    this.teams.forEach(teamSprite => {
      if (teamSprite.numberText) {
        teamSprite.numberText.x = teamSprite.x
        teamSprite.numberText.y = teamSprite.y
      }
    })
  }

  updateTeamNumber(teamId, newNumber) {
    // Update a specific team's number text without recreating the sprite
    const teamSprite = this.teams.find(t => t.teamData?.id === teamId)
    if (teamSprite && teamSprite.numberText) {
      teamSprite.numberText.setText(newNumber.toString())
    }
  }

  updateTeamPositionInStore(teamId, x, y) {
    // This will be called from the React component
    // The actual store update will happen in the Field component
    if (this.onTeamPositionUpdate) {
      this.onTeamPositionUpdate(teamId, x, y)
    }
  }

  updateTeamPosition(teamId, x, y) {
    // Skip position updates if teams were just updated to preserve visual positions
    if (this.teamsJustUpdated) {
      console.log('Skipping updateTeamPosition - teams just updated')
      return
    }
    
    // Update a specific team's position without recreating the sprite
    const teamSprite = this.teams.find(t => t.teamData.id === teamId)
    if (teamSprite) {
      teamSprite.x = x
      teamSprite.y = y
      // Update number text position
      if (teamSprite.numberText) {
        teamSprite.numberText.x = x
        teamSprite.numberText.y = y
      }
    }
  }

  setTeamPositionUpdateCallback(callback) {
    this.onTeamPositionUpdate = callback
  }

  removeTeamFromStore(teamId) {
    if (this.onTeamRemove) {
      this.onTeamRemove(teamId)
    }
  }

  setTeamRemoveCallback(callback) {
    this.onTeamRemove = callback
  }

  updatePropPositionInStore(propId, x, y) {
    // This will be called from the React component
    // The actual store update will happen in the Field component
    if (this.onPropPositionUpdate) {
      this.onPropPositionUpdate(propId, x, y)
    }
  }

  updatePropPosition(propId, x, y) {
    // Update a specific prop's position without recreating the sprite
    const propSprite = this.props.find(p => p.propData.id === propId)
    if (propSprite) {
      propSprite.x = x
      propSprite.y = y
    }
  }

  setPropPositionUpdateCallback(callback) {
    this.onPropPositionUpdate = callback
  }

  removePropFromStore(propId) {
    if (this.onPropRemove) {
      this.onPropRemove(propId)
    }
  }

  setPropRemoveCallback(callback) {
    this.onPropRemove = callback
  }

  setEntitySelectCallback(callback) {
    this.onEntitySelect = callback
  }

  setCurrentSelectionGetter(getter) {
    this.getCurrentSelectionFromStore = getter
  }

  // Clear selection visual from a single sprite
  clearSpriteSelection(sprite) {
    if (!sprite) return
    
    // Reset visual properties
    sprite.setAlpha(1.0)
    sprite.isSelected = false
    
    // For soccer ball, restore original scale if it exists, otherwise use 1.0
    if (sprite.propData?.type === 'soccer-ball' && sprite.originalScaleX && sprite.originalScaleY) {
      sprite.setScale(sprite.originalScaleX, sprite.originalScaleY)
    } else {
      sprite.setScale(1.0)
    }
    
    // Remove selection border
    if (sprite.selectionBorder) {
      sprite.selectionBorder.destroy()
      sprite.selectionBorder = null
    }
  }

  // Apply selection visual to a single sprite
  applySpriteSelection(sprite) {
    if (!sprite) return
    
    // Mark as selected
    sprite.isSelected = true
    
    // Add selection effect - handle scale differently for soccer balls
    if (sprite.propData?.type === 'soccer-ball' && sprite.originalScaleX && sprite.originalScaleY) {
      // For soccer balls, apply 1.1 scale relative to original scale
      sprite.setScale(sprite.originalScaleX * 1.1, sprite.originalScaleY * 1.1)
    } else {
      // For all other sprites, use standard 1.1 scale
      sprite.setScale(1.1)
    }
    sprite.setAlpha(0.9)
    
    // Add selection border
    const border = this.add.graphics()
    border.lineStyle(3, 0x00FF00, 1) // Green border
    
    // Handle different sprite types for border positioning
    if (sprite.propData?.type === 'cone') {
      // For cones, draw a circle that better fits the triangular shape
      // Cones are positioned with their center at the base, so adjust the circle position
      border.strokeCircle(sprite.x - 10, sprite.y - 13, 18) // Offset up and left, smaller radius
    } else {
      // For all other sprites (teams, soccer ball), use standard circle
      border.strokeCircle(sprite.x, sprite.y, 20)
    }
    
    border.setDepth(sprite.depth + 1)
    sprite.selectionBorder = border
  }

  // Clear all selection visuals from all sprites
  clearAllSelectionVisuals() {
    this.teams.forEach(teamSprite => this.clearSpriteSelection(teamSprite))
    this.props.forEach(propSprite => this.clearSpriteSelection(propSprite))
    
    // Clear the current selected entity reference
    this.currentSelectedEntity = null
  }

  // Update visual selection state for all entities
  updateSelectionVisuals(selectedEntity) {
    // Don't apply selection visuals if we're currently recording or have focus set
    if (this.isRecording || this.focusedTeamId !== null || this.focusedPropId !== null) {
      return
    }
    
    // Store the current selected entity
    this.currentSelectedEntity = selectedEntity
    
    // Clear all existing selections
    this.clearAllSelectionVisuals()
    
    // Apply selection to the new entity if specified
    if (selectedEntity) {
      let selectedSprite = null
      
      if (selectedEntity.type === 'team') {
        selectedSprite = this.teams.find(t => t.teamData?.id === selectedEntity.id)
      } else if (selectedEntity.type === 'prop') {
        selectedSprite = this.props.find(p => p.propData?.id === selectedEntity.id)
      }
      
      if (selectedSprite) {
        this.applySpriteSelection(selectedSprite)
      }
    }
  }

  startRecording(targetId) {
    this.isRecording = true
    this.recordingTeamId = targetId // Keep the same variable name for compatibility
    this.recordingPath = []
    this.recordingStartTime = Date.now()
    console.log('Started recording for target:', targetId)
    
    // Enable drag functionality only for the recording target, lock all others
    this.setDragEnabled(true, targetId)
  }

  stopRecording() {
    this.isRecording = false
    this.recordingTeamId = null
    console.log('Stopped recording, path length:', this.recordingPath ? this.recordingPath.length : 0)
    
    // Re-enable all dragging when recording stops
    this.setDragEnabled(true)
    
    return this.recordingPath
  }

  addToRecordingPath(x, y) {
    if (this.isRecording && this.recordingPath && 
        typeof x === 'number' && typeof y === 'number' && 
        !isNaN(x) && !isNaN(y)) {
      // Use relative time from start of recording
      const relativeTime = (Date.now() - this.recordingStartTime) / 1000
      this.recordingPath.push({ x, y, time: relativeTime })
    }
  }

  handleResize(gameSize, baseSize, displaySize, resolution) {
    // Update camera bounds when window is resized - maintain expanded bounds for panning
    this.cameras.main.setBounds(-gameSize.width, -gameSize.height, gameSize.width * 3, gameSize.height * 3)
    
    // Optionally refit the field to screen
    // this.fitFieldToScreen()
  }
}
